import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEventHandler } from 'react';
import '../App.css';

import { CreateModal } from './CreateModal';
import { EnumEditor } from './EnumEditor';
import { GameSchemaWizard } from './GameSchemaWizard';
import { TypeEditor } from './TypeEditor';
import { TypeTreeNode } from './TypeTreeNode';
import { downloadSchemaFile } from '../utils/download';
import { buildTypeTree, createId, getStringValues, normalizeName } from '../utils/modelUtils';
import { createEmptySchema, normalizeSchema, schemaToEnums, schemaToTypes } from '../utils/schemaUtils';
import { upsertEnumDef, upsertTypeDef, removeDef } from '../utils/schemaMutations';
import { validateSchema } from '../utils/validation';
import type {
  EnumDef,
  FieldDef,
  HistoryState,
  Selection,
  StatusMessage,
  StoredState,
  TypeDef,
  VersionSnapshot,
  ValidationIssue,
} from '../types/model';
import type { JsonSchema } from '../types/schema';
import type { WizardConfig } from '../types/wizard';
import type { DataApi } from '../api/types';
import { FakeApi } from '../api/fakeApi';

export type VisualDataModelerProps = {
  api?: DataApi;
};

export const VisualDataModeler = ({ api }: VisualDataModelerProps) => {
  const defaultApiRef = useRef<DataApi | null>(null);
  if (!defaultApiRef.current) {
    defaultApiRef.current = new FakeApi();
  }
  const resolvedApi = api ?? defaultApiRef.current;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [history, setHistory] = useState<HistoryState<JsonSchema>>(() => ({
    past: [],
    present: createEmptySchema(),
    future: [],
  }));
  const [selection, setSelection] = useState<Selection>(null);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState<'type' | 'enum'>('type');
  const [versions, setVersions] = useState<VersionSnapshot[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [enforceIndexedInheritance, setEnforceIndexedInheritance] = useState(true);
  const [clearedDefaultFields, setClearedDefaultFields] = useState<Record<string, boolean>>({});
  const [jsonDefaultErrors, setJsonDefaultErrors] = useState<Record<string, boolean>>({});
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [wizardConfig, setWizardConfig] = useState<WizardConfig | null>(null);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardDismissed, setWizardDismissed] = useState(false);

  const schema = history.present;
  const types = useMemo(() => schemaToTypes(schema), [schema]);
  const enums = useMemo(() => schemaToEnums(schema), [schema]);
  const typeTree = useMemo(() => buildTypeTree(types), [types]);
  const isSchemaEmpty = Object.keys(schema.$defs ?? {}).length === 0;

  const validationIssues = useMemo(
    () => validateSchema(schema, { enforceIndexedInheritance }, clearedDefaultFields, jsonDefaultErrors),
    [schema, enforceIndexedInheritance, clearedDefaultFields, jsonDefaultErrors]
  );

  const hasErrors = validationIssues.some((issue) => issue.level === 'error');

  const commit = useCallback((updater: (prev: JsonSchema) => JsonSchema) => {
    setHistory((prev) => {
      const next = updater(prev.present);
      return { past: [...prev.past, prev.present], present: next, future: [] };
    });
  }, []);

  useEffect(() => {
    let active = true;
    resolvedApi
      .loadState()
      .then((stored) => {
        if (!active) {
          return;
        }
        const normalizedSchema = stored?.schema ? normalizeSchema(stored.schema) : createEmptySchema();
        setHistory({ past: [], present: normalizedSchema, future: [] });
        setVersions(stored?.versions ?? []);
        setEnforceIndexedInheritance(stored?.enforceIndexedInheritance ?? true);
        setSelection(null);
        setFocusFieldId(null);
        setClearedDefaultFields({});
        setJsonDefaultErrors({});
        setIsReady(true);
      })
      .catch(() => {
        if (active) {
          setIsReady(true);
        }
      });
    return () => {
      active = false;
    };
  }, [resolvedApi]);

  useEffect(() => {
    let active = true;
    fetch('/game_schema_wizard_questionnaire_config.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load wizard config');
        }
        return response.json();
      })
      .then((data: WizardConfig) => {
        if (active) {
          setWizardConfig(data);
          setWizardError(null);
        }
      })
      .catch(() => {
        if (active) {
          setWizardConfig(null);
          setWizardError('Wizard configuration could not be loaded.');
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isReady) {
      return;
    }
    const state: StoredState = {
      schema,
      versions,
      enforceIndexedInheritance,
    };
    void resolvedApi.saveState(state);
  }, [schema, versions, enforceIndexedInheritance, resolvedApi, isReady]);

  useEffect(() => {
    const fieldIds = new Set(types.flatMap((type) => type.fields.map((field) => field.id)));
    setClearedDefaultFields((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        if (fieldIds.has(key)) {
          next[key] = true;
        }
      });
      return next;
    });
    setJsonDefaultErrors((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        if (fieldIds.has(key)) {
          next[key] = true;
        }
      });
      return next;
    });
  }, [types]);

  useEffect(() => {
    if (!selection) {
      return;
    }
    if (selection.kind === 'type' && !types.some((type) => type.id === selection.id)) {
      setSelection(null);
    }
    if (selection.kind === 'enum' && !enums.some((entry) => entry.id === selection.id)) {
      setSelection(null);
    }
  }, [types, enums, selection]);

  useEffect(() => {
    if (!isReady || !wizardConfig || wizardDismissed || isWizardOpen) {
      return;
    }
    if (isSchemaEmpty) {
      setIsWizardOpen(true);
    }
  }, [isReady, wizardConfig, wizardDismissed, isWizardOpen, isSchemaEmpty]);

  useEffect(() => {
    if (!isResizingSidebar) {
      document.body.classList.remove('resizing');
      return;
    }
    document.body.classList.add('resizing');
    return () => {
      document.body.classList.remove('resizing');
    };
  }, [isResizingSidebar]);

  useEffect(() => {
    if (!isResizingSidebar) {
      return;
    }

    const handleMove = (event: globalThis.MouseEvent) => {
      if (!workspaceRef.current) {
        return;
      }
      const rect = workspaceRef.current.getBoundingClientRect();
      const rawWidth = event.clientX - rect.left;
      const minWidth = 200;
      const maxWidth = 420;
      const next = Math.max(minWidth, Math.min(maxWidth, rawWidth));
      setSidebarWidth(next);
    };

    const handleUp = () => setIsResizingSidebar(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isResizingSidebar]);

  const clearDefaultWarning = useCallback((fieldId: string) => {
    setClearedDefaultFields((prev) => {
      if (!prev[fieldId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const handleJsonErrorChange = useCallback((fieldId: string, hasError: boolean) => {
    setJsonDefaultErrors((prev) => {
      if (prev[fieldId] === hasError) {
        return prev;
      }
      const next = { ...prev };
      if (hasError) {
        next[fieldId] = true;
      } else {
        delete next[fieldId];
      }
      return next;
    });
  }, []);

  const handleSave = () => {
    if (hasErrors) {
      setStatusMessage({ type: 'error', text: 'Fix validation errors before saving.' });
      return;
    }
    const versionId = createId();
    const timestamp = new Date().toISOString();
    const nextSchema = { ...schema, 'x-version': versionId, 'x-timestamp': timestamp };
    commit(() => nextSchema);
    setVersions((prev) => [{ id: versionId, timestamp, schema: nextSchema }, ...prev]);
    setStatusMessage({ type: 'success', text: 'Model saved locally and versioned.' });
  };

  const handleExport = () => {
    if (hasErrors) {
      setStatusMessage({ type: 'error', text: 'Fix validation errors before exporting.' });
      return;
    }
    downloadSchemaFile(schema);
    setStatusMessage({ type: 'success', text: 'Schema exported.' });
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile: ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Invalid JSON');
        }
        const candidate = parsed as JsonSchema;
        if (!candidate || typeof candidate !== 'object' || typeof candidate.$defs !== 'object') {
          throw new Error('Missing schema definitions');
        }
        const importedSchema = normalizeSchema(candidate);
        setHistory({ past: [], present: importedSchema, future: [] });
        setVersions([]);
        setSelection(null);
        setFocusFieldId(null);
        setClearedDefaultFields({});
        setJsonDefaultErrors({});
        setStatusMessage({ type: 'success', text: 'Schema imported.' });
      } catch {
        setStatusMessage({ type: 'error', text: 'Import failed. Provide a valid JSON Schema.' });
      }
    };
    reader.readAsText(file);
  };

  const handleOpenWizard = () => {
    if (!wizardConfig) {
      setStatusMessage({ type: 'error', text: wizardError ?? 'Wizard configuration is unavailable.' });
      return;
    }
    if (!isSchemaEmpty) {
      const confirmed = window.confirm('Starting the wizard will replace the current schema. Continue?');
      if (!confirmed) {
        return;
      }
    }
    setIsWizardOpen(true);
    setWizardDismissed(false);
  };

  const handleWizardCancel = () => {
    setIsWizardOpen(false);
    setWizardDismissed(true);
    setStatusMessage({ type: 'info', text: 'Wizard dismissed. Continue manually.' });
  };

  const handleWizardComplete = (nextSchema: JsonSchema) => {
    setHistory({ past: [], present: nextSchema, future: [] });
    setVersions([]);
    setSelection(null);
    setFocusFieldId(null);
    setClearedDefaultFields({});
    setJsonDefaultErrors({});
    setIsWizardOpen(false);
    setWizardDismissed(true);
    setStatusMessage({ type: 'success', text: 'Wizard generated a new schema.' });
  };

  const updateType = (typeId: string, updater: (type: TypeDef) => TypeDef) => {
    commit((prev) => {
      const currentTypes = schemaToTypes(prev);
      const target = currentTypes.find((type) => type.id === typeId);
      if (!target) {
        return prev;
      }
      const nextType = updater(target);
      return upsertTypeDef(prev, nextType);
    });
  };

  const updateEnum = (enumId: string, updater: (entry: EnumDef) => EnumDef) => {
    commit((prev) => {
      const currentEnums = schemaToEnums(prev);
      const target = currentEnums.find((entry) => entry.id === enumId);
      if (!target) {
        return prev;
      }
      const nextEnum = updater(target);
      return upsertEnumDef(prev, nextEnum);
    });
  };

  const isFieldNameAvailable = (name: string, fields: FieldDef[], fieldId: string) => {
    const key = normalizeName(name);
    if (!key) {
      return true;
    }
    return !fields.some((field) => field.id !== fieldId && normalizeName(field.name) === key);
  };

  const updateField = (typeId: string, fieldId: string, updater: (field: FieldDef) => FieldDef) => {
    updateType(typeId, (type) => {
      const fields = type.fields.map((field) => (field.id === fieldId ? updater(field) : field));
      const current = type.fields.find((field) => field.id === fieldId);
      const updated = fields.find((field) => field.id === fieldId);
      if (current && updated && current.name !== updated.name) {
        if (!isFieldNameAvailable(updated.name, fields, fieldId)) {
          return type;
        }
      }
      return { ...type, fields };
    });
  };

  const addType = (type: TypeDef) => {
    commit((prev) => upsertTypeDef(prev, type));
    setSelection({ kind: 'type', id: type.id });
  };

  const addEnum = (entry: EnumDef) => {
    commit((prev) => upsertEnumDef(prev, entry));
    setSelection({ kind: 'enum', id: entry.id });
  };

  const addField = (typeId: string) => {
    const currentType = types.find((type) => type.id === typeId);
    const existingKeys = new Set(
      (currentType?.fields ?? []).map((field) => normalizeName(field.name)).filter(Boolean)
    );
    let index = (currentType?.fields.length ?? 0) + 1;
    let fallbackName = `field${index}`;
    while (existingKeys.has(normalizeName(fallbackName))) {
      index += 1;
      fallbackName = `field${index}`;
    }
    const newField: FieldDef = {
      id: createId(),
      name: fallbackName,
      type: { kind: 'primitive', primitive: 'string' },
      isArray: false,
      isRequired: false,
      defaultValue: null,
      constraints: {},
    };
    updateType(typeId, (type) => ({ ...type, fields: [...type.fields, newField] }));
    setFocusFieldId(newField.id);
  };

  const attemptDeleteType = (typeId: string) => {
    const target = types.find((type) => type.id === typeId);
    if (!target) {
      return;
    }
    const children = types.filter((type) => type.parentTypeId === typeId);
    if (children.length > 0) {
      const list = children.map((child) => `- ${child.name || 'Unnamed type'}`).join('\n');
      window.alert(`Cannot delete "${target.name || 'Unnamed type'}". It has child types:\n${list}`);
      return;
    }
    const usages = types.flatMap((type) =>
      type.fields
        .filter(
          (field) =>
            (field.type.kind === 'type' || field.type.kind === 'reference') && field.type.typeId === typeId
        )
        .map((field) => `${type.name || 'Unnamed type'} -> ${field.name || 'Unnamed field'}`)
    );
    if (usages.length > 0) {
      const list = usages.map((usage) => `- ${usage}`).join('\n');
      window.alert(`Cannot delete "${target.name || 'Unnamed type'}". It is used by:\n${list}`);
      return;
    }
    if (!window.confirm(`Delete type "${target.name || 'Unnamed type'}"? This cannot be undone.`)) {
      return;
    }
    commit((prev) => removeDef(prev, typeId));
    setSelection(null);
  };

  const attemptDeleteEnum = (enumId: string) => {
    const target = enums.find((entry) => entry.id === enumId);
    if (!target) {
      return;
    }
    const usages = types.flatMap((type) =>
      type.fields
        .filter((field) => field.type.kind === 'enum' && field.type.enumId === enumId)
        .map((field) => `${type.name || 'Unnamed type'} -> ${field.name || 'Unnamed field'}`)
    );
    if (usages.length > 0) {
      const list = usages.map((usage) => `- ${usage}`).join('\n');
      window.alert(`Cannot delete "${target.name || 'Unnamed enum'}". It is used by:\n${list}`);
      return;
    }
    if (!window.confirm(`Delete enum "${target.name || 'Unnamed enum'}"? This cannot be undone.`)) {
      return;
    }
    commit((prev) => removeDef(prev, enumId));
    setSelection(null);
  };

  const attemptDeleteField = (typeId: string, fieldId: string) => {
    if (!window.confirm('Delete this field? This cannot be undone.')) {
      return;
    }
    updateType(typeId, (type) => ({
      ...type,
      fields: type.fields.filter((field) => field.id !== fieldId),
    }));
    setClearedDefaultFields((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  };

  const attemptDeleteEnumValue = (enumId: string, valueId: string) => {
    const targetEnum = enums.find((entry) => entry.id === enumId);
    if (!targetEnum) {
      return;
    }
    const affectedFields = types.flatMap((type) =>
      type.fields
        .filter((field) => field.type.kind === 'enum' && field.type.enumId === enumId)
        .filter((field) => {
          if (field.isArray && Array.isArray(field.defaultValue)) {
            return getStringValues(field.defaultValue).includes(valueId);
          }
          return field.defaultValue === valueId;
        })
        .map((field) => ({ type, field }))
    );
    if (affectedFields.length > 0) {
      const list = affectedFields
        .map((entry) => `- ${entry.type.name || 'Unnamed type'} -> ${entry.field.name || 'Unnamed field'}`)
        .join('\n');
      const confirmed = window.confirm(
        `Deleting this enum value will clear defaults for:\n${list}\n\nContinue?`
      );
      if (!confirmed) {
        return;
      }
    } else if (!window.confirm('Delete this enum value? This cannot be undone.')) {
      return;
    }

    commit((prev) => {
      const currentTypes = schemaToTypes(prev);
      const currentEnums = schemaToEnums(prev);
      let next = prev;
      currentTypes.forEach((type) => {
        const updatedFields = type.fields.map((field) => {
          if (field.type.kind !== 'enum' || field.type.enumId !== enumId) {
            return field;
          }
          const shouldClear = field.isArray
            ? getStringValues(field.defaultValue).includes(valueId)
            : field.defaultValue === valueId;
          if (!shouldClear) {
            return field;
          }
          return { ...field, defaultValue: null };
        });
        next = upsertTypeDef(next, { ...type, fields: updatedFields });
      });
      const updatedEnum = currentEnums.find((entry) => entry.id === enumId);
      if (updatedEnum) {
        next = upsertEnumDef(next, {
          ...updatedEnum,
          values: updatedEnum.values.filter((value) => value.id !== valueId),
        });
      }
      return next;
    });

    setClearedDefaultFields((prev) => {
      const next = { ...prev };
      affectedFields.forEach((entry) => {
        next[entry.field.id] = true;
      });
      return next;
    });
  };

  const handleRestoreVersion = (snapshot: VersionSnapshot) => {
    if (!window.confirm(`Restore version ${snapshot.id}? Current changes will move to undo history.`)) {
      return;
    }
    commit(() => snapshot.schema);
    setSelection(null);
    setStatusMessage({ type: 'info', text: 'Version restored. Use undo to return.' });
  };

  const handleIssueNavigate = (issue: ValidationIssue) => {
    if (issue.location?.typeId) {
      setSelection({ kind: 'type', id: issue.location.typeId });
    }
    if (issue.location?.enumId) {
      setSelection({ kind: 'enum', id: issue.location.enumId });
    }
    if (issue.location?.fieldId) {
      setFocusFieldId(issue.location.fieldId);
    }
  };

  const handleCreate = () => {
    setIsCreateOpen(true);
    setStatusMessage(null);
  };

  const typeOptions = [...types].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
  const enumOptions = [...enums].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

  const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsResizingSidebar(true);
  };

  if (!isReady) {
    return (
      <div className="app-shell">
        <main className="canvas">
          <section className="panel hero">
            <h2>Loading modeler...</h2>
            <p>Preparing your schema workspace.</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="toolbar">
        <div className="toolbar-title">
          <div className="title">Visual Data Modeler</div>
          <div className="subtitle">Model authority workspace</div>
        </div>
        <div className="toolbar-actions">
          <button className="button primary" type="button" onClick={handleCreate}>
            Create
          </button>
          {wizardConfig && (
            <button className="button" type="button" onClick={handleOpenWizard} disabled={isWizardOpen}>
              Wizard
            </button>
          )}
          <button className="button" type="button" onClick={handleSave} disabled={hasErrors}>
            Save
          </button>
          <span className="toolbar-separator" aria-hidden="true" />
          <button className="button" type="button" onClick={handleExport} disabled={hasErrors}>
            Export
          </button>
          <button className="button" type="button" onClick={handleImport}>
            Import
          </button>
          <input
            ref={fileInputRef}
            className="file-input"
            type="file"
            accept="application/schema+json,application/json"
            onChange={handleImportFile}
          />
        </div>
        <label className="toolbar-toggle">
          <input
            type="checkbox"
            checked={enforceIndexedInheritance}
            onChange={(event) => setEnforceIndexedInheritance(event.target.checked)}
          />
          <span>Enforce indexed inheritance</span>
        </label>
      </header>
      <div className="workspace" ref={workspaceRef} style={{ ['--sidebar-width' as string]: `${sidebarWidth}px` }}>
        <aside className="sidebar">
          <div className="sidebar-section">
            <div className="sidebar-meta">
              <div className="meta-label">Current version</div>
              <div className="meta-value">{schema['x-version'] ?? 'unsaved'}</div>
              <div className="meta-label">Last saved</div>
              <div className="meta-value">{schema['x-timestamp'] ?? 'not yet'}</div>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="tree-header">
              <span>Types</span>
              <span className="tree-count">{types.length}</span>
            </div>
            <div className="tree-list">
              {typeTree.length === 0 && <div className="muted">No types yet.</div>}
              {typeTree.map((node) => (
                <TypeTreeNode
                  key={node.type.id}
                  node={node}
                  depth={0}
                  selectedId={selection?.kind === 'type' ? selection.id : null}
                  onSelect={(id) => setSelection({ kind: 'type', id })}
                />
              ))}
            </div>
          </div>
          <div className="sidebar-section">
            <div className="tree-header">
              <span>Enums</span>
              <span className="tree-count">{enums.length}</span>
            </div>
            <div className="tree-list">
              {enumOptions.length === 0 && <div className="muted">No enums yet.</div>}
              {enumOptions.map((entry, index) => (
                <button
                  key={entry.id}
                  className={`tree-item ${selection?.kind === 'enum' && selection.id === entry.id ? 'active' : ''}`}
                  style={{ ['--stagger' as string]: index }}
                  type="button"
                  onClick={() => setSelection({ kind: 'enum', id: entry.id })}
                >
                  <span>{entry.name || 'Unnamed enum'}</span>
                  <span className="meta-pill">{entry.values.length} values</span>
                </button>
              ))}
            </div>
          </div>
        </aside>
        <div className="sidebar-resizer" role="separator" aria-orientation="vertical" onMouseDown={handleResizeStart} />
        <main className="canvas">
          {statusMessage && (
            <div className={`status-banner ${statusMessage.type}`}>
              <span>{statusMessage.text}</span>
              <button type="button" className="button ghost" onClick={() => setStatusMessage(null)}>
                Close
              </button>
            </div>
          )}
          {isWizardOpen ? (
            wizardConfig ? (
              <GameSchemaWizard config={wizardConfig} onCancel={handleWizardCancel} onComplete={handleWizardComplete} />
            ) : (
              <section className="panel wizard-panel">
                <h2>Wizard unavailable</h2>
                <p className="muted">{wizardError ?? 'Wizard configuration could not be loaded.'}</p>
                <div className="wizard-actions">
                  <button type="button" className="button ghost" onClick={handleWizardCancel}>
                    Back to editor
                  </button>
                </div>
              </section>
            )
          ) : (
            <>
              {!selection && (
                <section className="panel hero">
                  <h2>Start shaping your model</h2>
                  <p>
                    Create a type or enum to begin. The model tree will grow as you define entities, inheritance, and
                    constraints.
                  </p>
                </section>
              )}

              {selection?.kind === 'type' && (
                <TypeEditor
                  typeDef={types.find((type) => type.id === selection.id) ?? null}
                  types={types}
                  enums={enums}
                  onUpdateType={updateType}
                  onUpdateField={updateField}
                  onAddField={addField}
                  onDeleteField={attemptDeleteField}
                  onDeleteType={attemptDeleteType}
                  onSelectType={(typeId) => setSelection({ kind: 'type', id: typeId })}
                  focusFieldId={focusFieldId}
                  onClearFieldFocus={() => setFocusFieldId(null)}
                  onJsonErrorChange={handleJsonErrorChange}
                  clearedDefaultFields={clearedDefaultFields}
                  onDefaultUpdated={clearDefaultWarning}
                  enforceIndexedInheritance={enforceIndexedInheritance}
                />
              )}

              {selection?.kind === 'enum' && (
                <EnumEditor
                  enumDef={enums.find((entry) => entry.id === selection.id) ?? null}
                  onUpdateEnum={updateEnum}
                  onDeleteEnum={attemptDeleteEnum}
                  onDeleteValue={attemptDeleteEnumValue}
                />
              )}

              <section className="panel validation-panel">
                <div className="panel-header">
                  <div className="panel-title">Validation</div>
                  <div className={`status-chip ${hasErrors ? 'error' : 'ok'}`}>
                    {hasErrors ? 'Errors found' : 'All checks pass'}
                  </div>
                </div>
                {validationIssues.length === 0 ? (
                  <p className="muted">Your model is consistent. Save when ready.</p>
                ) : (
                  <ul className="issue-list">
                    {validationIssues.map((issue, index) => (
                      <li key={issue.id} className="issue-item" style={{ ['--stagger' as string]: index }}>
                        <span>{issue.message}</span>
                        {issue.location && (
                          <button type="button" className="button ghost" onClick={() => handleIssueNavigate(issue)}>
                            Go
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </main>
      </div>

      {isHistoryOpen && (
        <div className="history-panel">
          <div className="panel-header">
            <div className="panel-title">Version History</div>
            <button type="button" className="button ghost" onClick={() => setIsHistoryOpen(false)}>
              Close
            </button>
          </div>
          <div className="panel-body">
            {versions.length === 0 ? (
              <p className="muted">No saved versions yet.</p>
            ) : (
              <ul className="history-list">
                {versions.map((entry, index) => (
                  <li key={entry.id} className="history-item" style={{ ['--stagger' as string]: index }}>
                    <div>
                      <div className="history-id">{entry.id}</div>
                      <div className="muted">{entry.timestamp}</div>
                    </div>
                    <button type="button" className="button" onClick={() => handleRestoreVersion(entry)}>
                      Restore
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {isCreateOpen && (
        <CreateModal
          mode={createMode}
          onModeChange={setCreateMode}
          types={types}
          existingTypeNames={types.map((type) => type.name)}
          existingEnumNames={enums.map((entry) => entry.name)}
          onClose={() => setIsCreateOpen(false)}
          onCreateType={addType}
          onCreateEnum={addEnum}
        />
      )}
    </div>
  );
};
